/**
 * Student Card Component
 *
 * A card-based display for student information, designed for parent-friendly viewing.
 * Shows photo placeholder, name, age, swim level, and medical alerts.
 */

import {
  Badge,
  Box,
  Card,
  Flex,
  HStack,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react"
import { FiAlertCircle, FiCalendar, FiMapPin, FiUser } from "react-icons/fi"

import { StudentActionsMenu } from "@/components/layout/Common/StudentActionsMenu"
import type { StudentResponse } from "@/types/student"

// Calculate age from date of birth
function calculateAge(dateOfBirth: string): number {
  const today = new Date()
  const birthDate = new Date(dateOfBirth)
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--
  }
  return age
}

// Format date of birth
function formatDateOfBirth(dateOfBirth: string): string {
  const date = new Date(dateOfBirth)
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

// Swim level colors
const swimLevelColors: Record<string, string> = {
  Bubble: "blue",
  "Tot (Ages 3-4 years)": "purple",
  Star: "yellow",
  Kicker: "green",
  Roller: "teal",
  Puller: "cyan",
  Charger: "orange",
  "Delta (Level 9 - Level 12)": "red",
  "Gamma (Level 13 - Level 16)": "pink",
  Challenge: "gray",
  "Intermediate(was Pre-Competitive)": "blue",
  "Advance (was Competitive)": "purple",
}

export interface StudentCardProps {
  student: StudentResponse
  /** Whether to show action menu */
  showActions?: boolean
  /** Click handler for the card */
  onClick?: () => void
}

export function StudentCard({
  student,
  showActions = true,
  onClick,
}: StudentCardProps) {
  const age = calculateAge(student.date_of_birth)
  const levelColor = student.current_swim_level
    ? swimLevelColors[student.current_swim_level] || "gray"
    : "gray"

  return (
    <Card.Root
      bg="white"
      borderRadius="xl"
      boxShadow="sm"
      borderWidth="1px"
      borderColor="gray.100"
      _hover={{
        boxShadow: "md",
        borderColor: "ui.main",
        transform: onClick ? "translateY(-2px)" : undefined,
      }}
      transition="all 0.2s"
      cursor={onClick ? "pointer" : "default"}
      onClick={onClick}
    >
      <Card.Body p={5}>
        <VStack align="stretch" gap={4}>
          {/* Header with avatar and name */}
          <Flex justify="space-between" align="start">
            <Flex align="center" gap={3}>
              {/* Photo placeholder */}
              <Box
                w="56px"
                h="56px"
                borderRadius="full"
                bg="ui.backgroundAlt"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                {student.photo_url ? (
                  <img
                    src={student.photo_url}
                    alt={`${student.first_name} ${student.last_name}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "9999px",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Icon color="ui.main" fontSize="2xl">
                    <FiUser />
                  </Icon>
                )}
              </Box>
              <Box>
                <Text fontWeight="semibold" fontSize="lg" color="gray.800">
                  {student.first_name} {student.last_name}
                </Text>
                <HStack fontSize="sm" color="gray.500" gap={3}>
                  <Text>{age} years old</Text>
                  {student.gender && (
                    <Text textTransform="capitalize">{student.gender}</Text>
                  )}
                </HStack>
              </Box>
            </Flex>

            {/* Actions menu */}
            {showActions && (
              <Box onClick={(e) => e.stopPropagation()}>
                <StudentActionsMenu student={student} />
              </Box>
            )}
          </Flex>

          {/* Swim Level */}
          {student.current_swim_level && (
            <Badge
              colorPalette={levelColor}
              variant="subtle"
              px={3}
              py={1}
              borderRadius="full"
              alignSelf="flex-start"
              fontSize="sm"
            >
              {student.current_swim_level}
            </Badge>
          )}

          {/* Details */}
          <VStack align="stretch" gap={2}>
            {/* Date of Birth */}
            <HStack fontSize="sm" color="gray.600">
              <Icon color="gray.400">
                <FiCalendar />
              </Icon>
              <Text>Born {formatDateOfBirth(student.date_of_birth)}</Text>
            </HStack>

            {/* Location Preference */}
            {student.preferred_location && (
              <HStack fontSize="sm" color="gray.600">
                <Icon color="gray.400">
                  <FiMapPin />
                </Icon>
                <Text>{student.preferred_location}</Text>
              </HStack>
            )}
          </VStack>

          {/* Medical Alert */}
          {student.has_asthma && (
            <Box
              bg="orange.50"
              borderRadius="md"
              p={3}
              borderLeftWidth="3px"
              borderLeftColor="orange.400"
            >
              <HStack color="orange.600" fontSize="sm">
                <Icon>
                  <FiAlertCircle />
                </Icon>
                <Text fontWeight="medium">Medical Alert: Asthma</Text>
              </HStack>
              <Text fontSize="xs" color="orange.500" mt={1}>
                Please notify instructor before class
              </Text>
            </Box>
          )}

          {/* Notes preview */}
          {student.notes && (
            <Text fontSize="sm" color="gray.500" lineClamp={2}>
              {student.notes}
            </Text>
          )}

          {/* Status */}
          <Flex justify="flex-end">
            <Badge
              colorPalette={student.is_active ? "green" : "gray"}
              variant="subtle"
              size="sm"
            >
              {student.is_active ? "Active" : "Inactive"}
            </Badge>
          </Flex>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

export default StudentCard
